/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable, runInAction } from 'mobx';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { FileView } from 'src/customText/appShell/dataTaggingPage/components/FilesViewDropdown';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextGptPredictionsStore } from 'src/customText/core/stores/CustomTextGptPredictionsStore';
import { isDocumentFiltered } from 'src/customText/core/utils/documentUtils';

export type FileFilter = 'none' | 'tagged' | 'autotagged' | 'untagged' | 'training' | 'testing';
export type LabelFilter = { name: string; id: string };

export type DocumentsFilter = { files: FileFilter[]; labels: LabelFilter[] };
export class SamplerStore {
    @observable public viewingOption: FileView = 'all';

    @observable public appliedFilters: DocumentsFilter = { files: [], labels: [] };

    @observable public searchQuery: string = '';

    @observable public filter: FileFilter = 'none';

    @observable private index = -1;

    @observable public documents: CustomTextDocumentMetadata[] = [];

    public readonly gptPredictionsStore: CustomTextGptPredictionsStore;

    constructor(gptPredictionsStore?: CustomTextGptPredictionsStore) {
        this.gptPredictionsStore = gptPredictionsStore;
    }

    /**
     * Sets documents.
     */
    @action
    public setDocuments(documents: CustomTextDocumentMetadata[], preserveIndex: boolean = false) {
        this.documents = documents;

        if (documents.length > 0 && !preserveIndex) {
            this.index = 0;
        }
    }

    /**
     * Sets the current document.
     */
    @action
    public setCurrentDocument(documentName: string) {
        this.changeViewingOption('single');
        this.index = this.filteredDocuments.findIndex(doc => doc.name === documentName);
    }

    /**
     * Changes documents view.
     */
    @action
    public changeViewingOption(viewingOption: FileView) {
        this.viewingOption = viewingOption;
        this.index = 0;
    }

    /**
     * Check if there are filters applied
     */
    @computed
    public get areFiltersApplied(): boolean {
        return Boolean(this.appliedFilters.files.length || this.appliedFilters.labels.length);
    }

    /**
     * Changes documents filter.
     */
    @action
    public changeAppliedFilters(appliedFilters: DocumentsFilter) {
        this.appliedFilters = appliedFilters;
        this.index = 0;
    }

    /**
     * Changes the search query for document names.
     */
    @action
    public setSearchQuery(searchQuery: string) {
        this.searchQuery = searchQuery;
    }

    /**
     * Goes to the next page.
     */
    @action
    public next(): void {
        if (this.index < this.documents.length - 1) {
            this.index++;
        }
    }

    /**
     * Goes to the previous page.
     */
    @action
    public previous(): void {
        if (this.index > 0) {
            this.index--;
        }
    }

    @computed
    public get currentDocument(): string {
        if (!this.isInitialized || this.filteredDocuments.length === 0) {
            return null;
        }

        let { index } = this;

        // E.g. labelled documents is active and we are in the last page,
        // and the user deletes all the labels that the document has.
        // At the time the index will out of bounds with the filtered docs.
        if (index > this.filteredDocuments.length - 1) {
            index = this.filteredDocuments.length - 1;
            setTimeout(() => {
                runInAction(() => {
                    this.index = index;
                });
            });
        }

        return this.filteredDocuments[index].name;
    }

    @computed
    public get noDocumentsAvailable(): boolean {
        return this.isInitialized && this.filteredDocuments.length === 0;
    }

    @computed
    public get isInitialized(): boolean {
        return this.index !== -1;
    }

    @computed
    public get isNextDisabled(): boolean {
        return this.index === this.filteredDocuments.length - 1 || !this.isInitialized || this.noDocumentsAvailable;
    }

    @computed
    public get isPreviousDisabled(): boolean {
        return this.index === 0 || !this.isInitialized || this.noDocumentsAvailable;
    }

    @computed
    public get filteredDocuments(): CustomTextDocumentMetadata[] {
        return substringSearchStrategy(
            this.documents.filter(document =>
                this.areFiltersApplied ? isDocumentFiltered(document, this.appliedFilters, this.gptPredictionsStore) : true
            ),
            this.searchQuery,
            'name'
        );
    }
}
